import { StarIcon } from "@/components/icons/star.icon";

// const STAR_PX = 22;

/** Clamps to [1, 5], then rounds to the nearest tenth (e.g. 4.14 → 4.1, 4.16 → 4.2). */
function roundRatingToNearestTenth(rating: number): number {
  if (!Number.isFinite(rating)) return 1;
  const clamped = Math.min(5, Math.max(1, rating));
  return Math.round(clamped * 10) / 10;
}

/** Per-star fill in [0, 1] for a 1–5 rating (e.g. 4.4 → [1,1,1,1,0.4]). */
function starFillFractions(roundedRating: number): number[] {
  return [1, 2, 3, 4, 5].map((index) =>
    Math.min(1, Math.max(0, roundedRating - (index - 1))),
  );
}

function RatingStar({
  fillFraction,
  fillColor,
  starSizePX,
}: {
  fillFraction: number;
  fillColor: string;
  starSizePX: number;
}) {
  const fraction = Math.min(1, Math.max(0, fillFraction));

  if (fraction >= 1) {
    return (
      <StarIcon
        width={starSizePX}
        height={starSizePX}
        className={`${fillColor} shrink-0`}
      />
    );
  }
  if (fraction <= 0) {
    return (
      <StarIcon
        width={starSizePX}
        height={starSizePX}
        className={`${fillColor}/30 shrink-0`}
      />
    );
  }

  return (
    <span className="relative inline-block h-[22px] w-[22px] shrink-0">
      <span
        className="absolute inset-y-0 left-0 overflow-hidden"
        style={{ width: `${fraction * 100}%` }}>
        <StarIcon
          width={starSizePX}
          height={starSizePX}
          className={`${fillColor}`}
        />
      </span>
    </span>
  );
}

export function RatingStars({
  rating,
  fillColor,
  starSizePX = 22,
}: {
  rating: number;
  fillColor: string;
  starSizePX?: number;
}) {
  const rounded = roundRatingToNearestTenth(rating);
  const label = `${rounded.toFixed(1)} out of 5 stars`;
  const fractions = starFillFractions(rounded);

  return (
    <div
      className="flex items-center gap-1"
      data-testid="highest-rated-app-stars"
      data-rounded-rating={rounded.toFixed(1)}
      role="img"
      aria-label={label}>
      {fractions.map((fraction, i) => (
        <RatingStar
          key={i}
          fillFraction={fraction}
          fillColor={fillColor}
          starSizePX={starSizePX}
        />
      ))}
    </div>
  );
}
